import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { ScrollView, YStack } from "tamagui";

import {
  getGetMedicationHistoryStatusQueryKey,
  useFetchMedicationHistory,
  useGetMedicationHistoryStatus,
  useSetActiveMedications,
} from "@/api/generated";
import { FetchRxHistoryWorkflowStatusStatus } from "@/api/generated/models";
import { useUpdateConsents } from "@/api/mutations/consent";
import {
  useIsChatMutationPending,
  useSendNoriMessage,
} from "@/api/mutations/conversation";
import { ConstrainedView } from "@/components";
import { TextTransitionAnimation } from "@/components/animated";
import { useFeatureFlag } from "@/components/FeatureFlagProvider";
import { useAskUserQuestionContainerStyles } from "@/conversation/AskUserQuestionsPanel";
import {
  NumberBadge,
  OptionBody,
  OptionBodyHoverWrapper,
  OptionSeparator,
} from "@/conversation/AskUserQuestionsPanel/OptionParts";
import PanelHeader from "@/conversation/AskUserQuestionsPanel/PanelHeader";
import { useCurrentConversationId } from "@/hooks/conversation";
import {
  PRE_ASYNC_COLLECTION_FLAG,
  useHasMedicationHistoryStoreHydrated,
  useMedicationHistoryStore,
} from "@/store/medicationHistory";
import ConsentFooter from "./ConsentFooter";
import { medicationHistoryCopyFor } from "./copy";
import MedicationPicker from "./MedicationPicker";
import PillLoader from "./PillLoader";

const CONSENT_OPTIONS = ["Yes", "No"] as const;

// With the ten fetching messages, the last one arrives about a minute in.
const FETCHING_MESSAGE_MS = 6000;

/**
 * The medication-history card: consent, the pull, then confirming the list.
 *
 * Separate from the questions panel, which any new message clears: the pull takes minutes and the
 * patient can keep chatting meanwhile.
 */
export default function MedicationHistoryPanel() {
  const conversationId = useCurrentConversationId();
  const hydrated = useHasMedicationHistoryStoreHydrated();
  const active = useMedicationHistoryStore((s) => s.active);
  const goTo = useMedicationHistoryStore((s) => s.goTo);
  const clear = useMedicationHistoryStore((s) => s.clear);
  const { sendNoriMessage } = useSendNoriMessage(conversationId);
  const replyStreaming = useIsChatMutationPending();
  const queryClient = useQueryClient();
  // Rechecked here because the store is persisted across a flag change.
  const { value: flagEnabled } = useFeatureFlag(
    PRE_ASYNC_COLLECTION_FLAG,
    false,
  );
  const containerStyles = useAskUserQuestionContainerStyles();

  const forThisConversation = active?.conversationId === conversationId;
  const step = active?.step;

  const { mutate: updateConsents, isPending: consentPending } =
    useUpdateConsents();
  const { mutate: startFetch } = useFetchMedicationHistory();
  const { mutate: setActiveMedications, isPending: saving } =
    useSetActiveMedications();

  const { data: status } = useGetMedicationHistoryStatus({
    query: {
      // Nothing to poll for until consent is granted and a fetch is running.
      enabled: Boolean(forThisConversation && step && step !== "consent"),
      // An errored query has no data, which is not the workflow finishing.
      refetchInterval: (query) =>
        query.state.data &&
        query.state.data.status !==
          FetchRxHistoryWorkflowStatusStatus.in_progress
          ? false
          : 5000,
      meta: { suppressErrorToast: true },
    },
  });

  // Only the workflow's status moves the card on; a failed query is not a finished pull.
  const settled =
    !!status &&
    status.status !== FetchRxHistoryWorkflowStatusStatus.in_progress;
  const pullFailed =
    status?.status === FetchRxHistoryWorkflowStatusStatus.failed;

  useEffect(() => {
    if (forThisConversation && step === "fetching" && settled) goTo("picker");
  }, [forThisConversation, step, settled, goTo]);

  if (!flagEnabled || !hydrated || !active || !forThisConversation) return null;
  // The card's event arrives before the reply that introduces it, so the question waits for the
  // reply to finish streaming.
  if (active.step === "consent" && replyStreaming) return null;

  const copy = medicationHistoryCopyFor(active.consentType);
  const header =
    active.step === "consent" ? copy.consentQuestion : copy.pickerQuestion;

  const answerConsent = (answer: (typeof CONSENT_OPTIONS)[number]) => {
    if (answer === "No") {
      clear();
      sendNoriMessage("I'd rather not pull my medication history.");
      return;
    }
    // Started here rather than on the next turn, which may be a while.
    updateConsents(
      { data: { consents: { [copy.consentType]: true } } },
      {
        onSuccess: () => {
          // The status endpoint reports the user's latest run, so polling before this fetch is
          // accepted would read the previous pull.
          startFetch(undefined, {
            onSuccess: () => {
              queryClient.removeQueries({
                queryKey: getGetMedicationHistoryStatusQueryKey(),
              });
              goTo("fetching");
            },
            onError: () => clear(),
          });
        },
        onError: () => clear(),
      },
    );
  };

  return (
    <ConstrainedView>
      <YStack {...containerStyles}>
        {active.step !== "fetching" && (
          <PanelHeader
            question={header}
            info={active.step === "consent" ? copy.info : undefined}
            index={0}
            total={1}
            onPrev={() => {}}
            onNext={() => {}}
          />
        )}
        <ScrollView flexGrow={0} flexShrink={1} flexBasis="auto" minHeight={0}>
          {active.step === "consent" && (
            <>
              {CONSENT_OPTIONS.map((option, index) => (
                <YStack key={option}>
                  {index > 0 && <OptionSeparator />}
                  <OptionBodyHoverWrapper>
                    <OptionBody
                      label={option}
                      checked={false}
                      showArrow
                      badge={<NumberBadge checked={false} index={index} />}
                      onPress={() => !consentPending && answerConsent(option)}
                    />
                  </OptionBodyHoverWrapper>
                </YStack>
              ))}
              <OptionSeparator />
              <ConsentFooter copy={copy} onSkip={() => answerConsent("No")} />
            </>
          )}

          {active.step === "fetching" && (
            <YStack
              alignItems="center"
              gap="$200"
              paddingHorizontal="$150"
              paddingVertical="$400"
            >
              <PillLoader />
              <TextTransitionAnimation
                isVisible
                texts={copy.fetchingMessages}
                icon={null}
                initialDelay={0}
                cycleDuration={FETCHING_MESSAGE_MS}
                textStyle={{ color: "$textSecondary", fontSize: "$sm" }}
              />
            </YStack>
          )}

          {active.step === "picker" && (
            <MedicationPicker
              medications={[
                ...(status?.medications ?? []),
                ...(status?.non_renewable_medications ?? []),
              ]}
              emptyDetail={pullFailed ? copy.failedDetail : copy.emptyDetail}
              saving={saving}
              onSkip={() => {
                clear();
                sendNoriMessage(
                  "I'd rather not confirm my medications right now.",
                );
              }}
              onConfirm={({ medicationHistoryIds, labels }) => {
                // Saved first: the next turn reads `active_medications`. Only Surescripts-verified
                // medications are saved; free text reaches the conversation through the message below.
                setActiveMedications(
                  {
                    data: {
                      medication_history_ids: medicationHistoryIds,
                      manual_medications: [],
                    },
                  },
                  {
                    onSuccess: () => {
                      clear();
                      sendNoriMessage(
                        labels.length
                          ? `I'm currently taking: ${labels.join(", ")}.`
                          : "I'm not currently taking any of those.",
                      );
                    },
                    onError: () => clear(),
                  },
                );
              }}
            />
          )}
        </ScrollView>
      </YStack>
    </ConstrainedView>
  );
}
